import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { wholesaleAuth } from '../services/api';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

const BLANK_ADDR = { label: '', addressLine: '', city: '', state: 'CA', zip: '', country: 'USA' };

function AddressFields({ prefix, values, onChange }) {
  const set = (k) => (e) => onChange({ ...values, [k]: e.target.value });
  return (
    <>
      <div className="mb-3">
        <label className="form-label">Location Label <span className="text-muted">(optional)</span></label>
        <input
          className="form-control"
          placeholder="e.g. Main Office, HQ"
          value={values.label}
          onChange={set('label')}
          id={`${prefix}_label`}
        />
      </div>
      <div className="mb-3">
        <label className="form-label">Street Address *</label>
        <input
          className="form-control"
          value={values.addressLine}
          onChange={set('addressLine')}
          required
          id={`${prefix}_addressLine`}
        />
      </div>
      <div className="row g-3">
        <div className="col-5">
          <label className="form-label">City *</label>
          <input
            className="form-control"
            value={values.city}
            onChange={set('city')}
            required
            id={`${prefix}_city`}
          />
        </div>
        <div className="col-3">
          <label className="form-label">State *</label>
          <select
            className="form-select"
            value={values.state}
            onChange={set('state')}
            id={`${prefix}_state`}
          >
            {US_STATES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="col-4">
          <label className="form-label">Zip *</label>
          <input
            className="form-control"
            value={values.zip}
            onChange={set('zip')}
            required
            pattern="\d{5}(-\d{4})?"
            id={`${prefix}_zip`}
          />
        </div>
      </div>
    </>
  );
}

export default function Register() {
  const navigate = useNavigate();

  // ── Core business / contact fields ──────────────────────────────────────────
  const [form, setForm] = useState({
    companyName: '', firstName: '', lastName: '', email: '',
    phone: '', password: '', abcLicenseNumber: '', resaleNumber: '',
  });
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  // ── Address fields ───────────────────────────────────────────────────────────
  const [billing,       setBilling]       = useState(BLANK_ADDR);
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [shipping,      setShipping]      = useState(BLANK_ADDR);

  // ── Optional billing contact email ───────────────────────────────────────────
  const [billingEmail, setBillingEmail] = useState('');

  // ── File uploads ─────────────────────────────────────────────────────────────
  const [files, setFiles] = useState({ abcLicenseFile: null, resaleCertificateFile: null });

  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const fd = new FormData();
      // Core fields
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      // Billing address
      Object.entries(billing).forEach(([k, v]) => fd.append(`billing_${k}`, v));
      // Shipping address
      fd.append('sameAsBilling', sameAsBilling ? 'true' : 'false');
      if (!sameAsBilling) {
        Object.entries(shipping).forEach(([k, v]) => fd.append(`shipping_${k}`, v));
      }
      // Billing email
      if (billingEmail.trim()) fd.append('billingEmail', billingEmail.trim());
      // Files
      if (files.abcLicenseFile)       fd.append('abcLicenseFile',       files.abcLicenseFile);
      if (files.resaleCertificateFile) fd.append('resaleCertificateFile', files.resaleCertificateFile);

      await wholesaleAuth.register(fd);
      navigate('/registration-pending');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 bg-light py-5">
      <div className="container" style={{ maxWidth: 680 }}>
        <h4 className="fw-bold mb-1">Apply for a Wholesale Account</h4>
        <p className="text-muted mb-4">Jeremy Wine Co. — Trade Buyers</p>

        {error && <div className="alert alert-danger py-2">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* ── Card 1: Business Information ── */}
          <div className="card mb-4">
            <div className="card-header bg-light fw-semibold">Business Information</div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label">Company Name *</label>
                <input className="form-control" value={form.companyName} onChange={set('companyName')} required />
              </div>
              <div className="mb-3">
                <label className="form-label">Phone</label>
                <input type="tel" className="form-control" value={form.phone} onChange={set('phone')} />
              </div>
              <div className="row g-3 mb-3">
                <div className="col-6">
                  <label className="form-label">ABC License # *</label>
                  <input className="form-control" value={form.abcLicenseNumber} onChange={set('abcLicenseNumber')} required />
                </div>
                <div className="col-6">
                  <label className="form-label">Resale # *</label>
                  <input className="form-control" value={form.resaleNumber} onChange={set('resaleNumber')} required />
                </div>
              </div>
              <div className="row g-3">
                <div className="col-6">
                  <label className="form-label">ABC License File <span className="text-muted">(optional)</span></label>
                  <input
                    type="file"
                    className="form-control"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={e => setFiles(f => ({ ...f, abcLicenseFile: e.target.files[0] || null }))}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label">Resale Certificate <span className="text-muted">(optional)</span></label>
                  <input
                    type="file"
                    className="form-control"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={e => setFiles(f => ({ ...f, resaleCertificateFile: e.target.files[0] || null }))}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ── Card 2: Primary Contact & Login ── */}
          <div className="card mb-4">
            <div className="card-header bg-light fw-semibold">Primary Contact &amp; Login</div>
            <div className="card-body">
              <div className="row g-3 mb-3">
                <div className="col-6">
                  <label className="form-label">First Name *</label>
                  <input className="form-control" value={form.firstName} onChange={set('firstName')} required />
                </div>
                <div className="col-6">
                  <label className="form-label">Last Name *</label>
                  <input className="form-control" value={form.lastName} onChange={set('lastName')} required />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Business Email *</label>
                <input type="email" className="form-control" value={form.email} onChange={set('email')} required />
                <div className="form-text">Used to log in to the wholesale portal.</div>
              </div>
              <div className="mb-0">
                <label className="form-label">Password *</label>
                <input type="password" className="form-control" value={form.password} onChange={set('password')} required minLength={8} />
                <div className="form-text">At least 8 characters.</div>
              </div>
            </div>
          </div>

          {/* ── Card 3: Billing Address ── */}
          <div className="card mb-4">
            <div className="card-header bg-light fw-semibold">Billing Address</div>
            <div className="card-body">
              <AddressFields prefix="billing" values={billing} onChange={setBilling} />
            </div>
          </div>

          {/* ── Card 4: Shipping Address ── */}
          <div className="card mb-4">
            <div className="card-header bg-light fw-semibold">Shipping Address</div>
            <div className="card-body">
              <div className="form-check mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="sameAsBilling"
                  checked={sameAsBilling}
                  onChange={e => setSameAsBilling(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="sameAsBilling">
                  Shipping address is the same as billing address
                </label>
              </div>
              {!sameAsBilling && (
                <AddressFields prefix="shipping" values={shipping} onChange={setShipping} />
              )}
            </div>
          </div>

          {/* ── Card 5: Billing Contact Email ── */}
          <div className="card mb-4">
            <div className="card-header bg-light fw-semibold">Billing Contact Email <span className="text-muted fw-normal small">(optional)</span></div>
            <div className="card-body">
              <div className="mb-0">
                <label className="form-label">Billing email address</label>
                <input
                  type="email"
                  className="form-control"
                  value={billingEmail}
                  onChange={e => setBillingEmail(e.target.value)}
                  placeholder="ap@yourcompany.com"
                />
                <div className="form-text">Used for invoices and account statements. Leave blank to use your login email.</div>
              </div>
            </div>
          </div>

          <button className="btn btn-primary w-100 mb-4" type="submit" disabled={loading}>
            {loading && <span className="spinner-border spinner-border-sm me-2" />}
            Submit Application
          </button>
        </form>

        <p className="text-center text-muted small mb-0">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
